import { useEffect, useRef, useState } from "react";
import { Mic, Square, X } from "lucide-react";
import { toast } from "sonner";

const Recognition = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

/**
 * TalkInput — a textarea with "Talk instead". Speech is transcribed into the same
 * field, stays editable, and is never submitted automatically. No audio is retained.
 */
export const TalkInput = ({ value, onChange, placeholder = "Write your response…", rows = 4, testid, className = "", warm = false, autoFocus = false }) => {
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const recRef = useRef(null);
  const baseRef = useRef("");
  const committedRef = useRef("");
  const supported = !!Recognition;

  const stop = (discard = false) => {
    const rec = recRef.current;
    recRef.current = null;
    if (rec) { rec.onend = null; try { rec.stop(); } catch { /* already stopped */ } }
    setListening(false);
    setInterim("");
    if (discard) onChange(baseRef.current);
  };

  useEffect(() => () => stop(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  const start = () => {
    if (!supported) { toast("Voice input isn't available in this browser — typing works just the same."); return; }
    const rec = new Recognition();
    rec.lang = navigator.language || "en-GB";
    rec.continuous = true;
    rec.interimResults = true;
    baseRef.current = value || "";
    committedRef.current = "";
    rec.onresult = (e) => {
      let finals = "", partial = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finals += t; else partial += t;
      }
      if (finals) {
        committedRef.current = (committedRef.current + " " + finals).trim();
        const joiner = baseRef.current && !/\s$/.test(baseRef.current) ? " " : "";
        onChange((baseRef.current + joiner + committedRef.current).trim());
      }
      setInterim(partial.trim());
    };
    rec.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        toast("Spark can't hear you yet — allow microphone access in your browser, or keep typing.");
      } else if (e.error !== "aborted" && e.error !== "no-speech") {
        toast("Voice paused. You can keep typing, or try again.");
      }
      stop(false);
    };
    rec.onend = () => { if (recRef.current === rec) stop(false); };
    recRef.current = rec;
    try { rec.start(); setListening(true); } catch { toast("Couldn't start listening. Please try again."); }
  };

  return (
    <div data-testid={testid ? `${testid}-wrap` : undefined} className={className}>
      <div className={`relative rounded-2xl border bg-white transition-colors ${listening ? "border-[#2C50A0]" : "border-[rgba(0,0,0,0.12)] focus-within:border-[#2C50A0]"}`}>
        <textarea
          data-testid={testid}
          rows={rows}
          value={value}
          autoFocus={autoFocus}
          onChange={e => onChange(e.target.value)}
          placeholder={listening ? "I'm listening…" : placeholder}
          className="w-full rounded-2xl bg-transparent px-5 py-4 text-base leading-relaxed focus:outline-none resize-y"
        />
        {listening && interim && (
          <p data-testid={testid ? `${testid}-interim` : undefined} className="px-5 pb-3 -mt-1 text-sm text-[#8A8A91]">{interim}…</p>
        )}
      </div>
      <div className="mt-2 flex items-center justify-between gap-3 min-h-[28px]">
        {!listening ? (
          <button
            type="button"
            data-testid={testid ? `${testid}-talk-btn` : "talk-btn"}
            onClick={start}
            className={`inline-flex items-center gap-1.5 text-xs transition-colors ${supported ? "text-[#8A8A91] hover:text-[#1F3D80]" : "text-[#D4D4D8] cursor-not-allowed"}`}
            title={supported ? "Speak instead of typing" : "Voice input isn't available in this browser"}
          >
            <Mic size={13} strokeWidth={1.75} /> {warm ? "Write it down, or talk it through" : "Talk instead"}
          </button>
        ) : (
          <div className="flex items-center gap-3" data-testid={testid ? `${testid}-listening` : "talk-listening"}>
            <span className="inline-flex items-center gap-2 text-xs text-[#1F3D80]">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#2C50A0] opacity-60 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#2C50A0]" />
              </span>
              I'm listening…
            </span>
            <button type="button" data-testid={testid ? `${testid}-stop-btn` : "talk-stop-btn"} onClick={() => stop(false)} className="inline-flex items-center gap-1 rounded-full border border-[rgba(0,0,0,0.14)] px-3 py-1 text-xs text-[#17171C] hover:border-[#2C50A0]">
              <Square size={10} strokeWidth={2} /> Stop
            </button>
            <button type="button" data-testid={testid ? `${testid}-cancel-btn` : "talk-cancel-btn"} onClick={() => stop(true)} className="inline-flex items-center gap-1 text-xs text-[#8A8A91] hover:text-[#17171C]">
              <X size={11} /> Cancel
            </button>
          </div>
        )}
        {listening && <span className="text-[11px] text-[#8A8A91]">You can edit anything before you continue.</span>}
      </div>
    </div>
  );
};

export default TalkInput;
